// Cross-platform contract (Android/iOS/web must match): instances are created
// at the deterministic id rectask-<ruleId>-<fireMillis> only if absent — never
// overwritten; the rule advance writes only nextFireAt/lastFiredAt/updatedAt
// as a partial merge; updatedAt is stamped at edit time.
//
// Android: domain/recurring/MaterializeRecurringTasks.kt
// iOS:     MaterializeRecurringTasks.swift

import { doc, runTransaction } from "firebase/firestore";
import { toDocId, toRefValue } from "../../utils/ids";
import { expandTitleTemplate } from "../../utils/titleTemplate";
import {
  nextFireAfter,
  notificationId,
  REMOTE_STATE_DELETE,
  REMOTE_STATE_SYNCED,
  NOTIFICATION_TYPE,
} from "../../utils/recurrence";

/**
 * Materialize every due rule. Each rule is claimed in a Firestore transaction
 * that (a) bails if the per-slot notification doc already exists (another
 * client — web, Android, or iOS — already materialized it), and (b) bails if
 * the rule's nextFireAt has already advanced past the slot. Whoever wins
 * creates the task (only if absent), advances the rule's schedule fields, and
 * writes the per-slot notification. The deterministic notification id
 * (rec-<ruleId>-<slot>) is the dedup marker.
 *
 * Pure of React state: everything it needs is passed in.
 *
 * @param {object} deps
 * @param {import("firebase/firestore").Firestore} deps.db Firestore instance
 * @param {string} deps.uid Current user's uid
 * @param {Array} deps.rules All known recurring rules (due ones are filtered here)
 * @param {Array} deps.labels All labels (for default action templates)
 * @param {Array} deps.actionTemplates All action templates
 */
export async function materializeDueRecurringRules({
  db,
  uid,
  rules,
  labels,
  actionTemplates,
}) {
  if (!db || !uid) return;
  const now = Date.now();
  const due = (rules || []).filter(
    (r) => r.enabled && r.remoteState !== REMOTE_STATE_DELETE && r.nextFireAt && r.nextFireAt <= now
  );
  if (due.length === 0) return;

  for (const rule of due) {
    const ruleRef = doc(db, `users/${uid}/recurring_rules`, String(rule.firestoreId || rule.id));
    try {
      await runTransaction(db, async (tx) => {
        // Firestore transactions require ALL reads before any write, so the
        // rule, the dedup marker, and the slot task are read up front.
        const ruleSnap = await tx.get(ruleRef);
        if (!ruleSnap.exists()) return;
        const r = ruleSnap.data();
        const slot = r.nextFireAt;
        if (!r.enabled || r.remoteState === REMOTE_STATE_DELETE || !slot || slot > now) return;

        // Per-slot notification = the materialization marker (matches
        // Android's idFor("rec-<ruleId>-<slot>")). If it exists AT ALL —
        // even dismissed (remoteState DELETE) — this slot already fired on
        // some client; skip so we don't re-materialize it.
        const notifId = notificationId(`rec-${r.id}-${slot}`);
        const notifRef = doc(db, `users/${uid}/notifications`, toDocId(notifId));
        const notifSnap = await tx.get(notifRef);
        if (notifSnap.exists()) return;

        // Deterministic, per-slot task id (same formula on every platform)
        // so the same rule slot can't produce two tasks across clients —
        // all write the same doc id.
        const newTaskId = notificationId(`rectask-${r.id}-${slot}`);
        const taskDocId = toDocId(newTaskId);
        const taskRef = doc(db, `users/${uid}/tasks`, taskDocId);
        // Create-if-absent: if the slot task already exists (even
        // soft-deleted), never overwrite it — user edits (e.g. removed
        // labels) must win. We still advance the rule schedule below.
        const taskSnap = await tx.get(taskRef);
        const taskExists = taskSnap.exists();

        // Jump straight to the next FUTURE occurrence (no backfill spam),
        // exactly like Android's nextFireAfter(afterMillis = now).
        const next = nextFireAfter(r.recurrenceType, r.dayOfWeek, r.dayOfMonth, r.hour, r.minute, now);

        const ruleLabelIds = (r.labelIds || []).map(Number);
        // Expand date tokens ($YYYY/$MMM/$DD) against this occurrence's slot,
        // so a daily rule "Tasks $YYYY-$MMM-$DD" spawns "Tasks 2026-May-18".
        const spawnedTitle = expandTitleTemplate(r.title, slot);

        if (!taskExists) {
          tx.set(taskRef, {
            id: newTaskId,
            title: spawnedTitle,
            description: "",
            projectId: toRefValue(r.projectId),
            usedAsNote: false,
            feedData: true,
            labelIds: ruleLabelIds,
            dueDate: r.setDueDate ? now : 0,
            taskState: r.targetState,
            estimatedTime: 0,
            aiTrackingEnabled: true,
            remoteState: REMOTE_STATE_SYNCED,
            firestoreId: taskDocId,
            updatedAt: now,
          });

          // Spawn the actions from any assigned label's default action
          // template. Deterministic per-(task, index) ids keep it idempotent
          // across clients. Only for a freshly created task — never rewrite
          // actions on an existing one.
          let actionOrder = 0;
          for (const lid of ruleLabelIds) {
            const label = (labels || []).find((l) => Number(l.id) === lid);
            if (!label || label.defaultActionTemplateId == null) continue;
            const tpl = (actionTemplates || []).find(
              (t) => Number(t.id) === Number(label.defaultActionTemplateId)
            );
            if (!tpl) continue;
            for (const actionLabel of tpl.actionLabels || []) {
              const text = (actionLabel || "").trim();
              if (!text) continue;
              const actId = notificationId(`recact-${newTaskId}-${actionOrder}`);
              const actDocId = toDocId(actId);
              tx.set(doc(db, `users/${uid}/actions`, actDocId), {
                actionId: actId,
                checkState: false,
                actionLabel: text,
                actionOrder,
                taskId: toRefValue(newTaskId),
                createdAt: now,
                updatedAt: now,
                remoteState: REMOTE_STATE_SYNCED,
                firestoreId: actDocId,
              });
              actionOrder++;
            }
          }
        }

        // Field-level advance: ONLY the schedule fields. Never rewrite the
        // whole rule doc — that would resurrect stale content fields (e.g.
        // labelIds) with a fresh updatedAt.
        tx.update(ruleRef, { nextFireAt: next, lastFiredAt: now, updatedAt: now });

        tx.set(notifRef, {
          id: notifId,
          type: NOTIFICATION_TYPE.RECURRING_CREATED,
          title: spawnedTitle,
          taskId: newTaskId,
          projectId: toRefValue(r.projectId),
          firedAt: now,
          read: false,
          remoteState: REMOTE_STATE_SYNCED,
          firestoreId: toDocId(notifId),
          updatedAt: now,
        });
      });
    } catch (err) {
      console.error("[materialize] rule", rule.id, "failed:", err);
    }
  }
}
